import { Feature, Point } from 'geojson';
import { takeUntil } from 'rxjs/operators';

import { MATERIAL_COLOR_LIST } from '@lib/common/constants';
import { GoogleMapPopup } from '@lib/geo/google/map';
import { SnPoint, SnLonLatLike, SnLonLat } from '@lib/geo/helpers';
import { SnBadgeConfig } from '@lib/ui/badge';
import { COLORS } from '@lib/ui/theme';

import { toGoogleMapLatLng, fromGoogleMapLatLng } from '../../helpers';
import { BaseMarker } from './base-marker';
import { GoogleMapIconMarker } from './icon-marker';
import { IGoogleMapMarkerEvents } from './marker-events';
import {
  GoogleMapMarkerIcon,
  IGoogleMapMarkerLabel,
  IGoogleMapMarkerOptions,
  IGoogleMapMarkerStyleOptions,
} from './marker-options';
import { GoogleMapMarkerType } from './marker-types';

const DEFAULT_OPTIONS: IGoogleMapMarkerOptions = {
  type: GoogleMapMarkerType.PIN,
  style: {
    fillColor: MATERIAL_COLOR_LIST.RED.get(600),
    fillOpacity: 1,
    strokeColor: COLORS['grey-0'],
    strokeWeight: 2,
    scale: 0.7,
  },
  draggable: false,
};

export class GoogleMapMarker extends BaseMarker<IGoogleMapMarkerEvents> {
  public feature: Feature<Point>;
  public timeouts = [];
  public interval;
  public pulseInterval;
  public iconLabel: any;
  public address: string;

  protected type: GoogleMapMarkerType;
  protected icon: GoogleMapMarkerIcon;
  protected badge: SnBadgeConfig;
  protected label: IGoogleMapMarkerLabel;
  protected style: IGoogleMapMarkerStyleOptions;
  protected draggable: boolean;

  private _iconAnchor: SnPoint;
  private readonly _iconMarker: GoogleMapIconMarker;
  private _popup: GoogleMapPopup;
  private _destination: SnLonLatLike;
  private _followMode: boolean;
  private _deltaLat: number;
  private _deltaLng: number;
  private _expectedMoves: number;
  private _actualMoves: number;

  private readonly _googleMapMarker: google.maps.Marker;
  private readonly _googleEventListenners: google.maps.MapsEventListener[] = [];

  constructor(options?: IGoogleMapMarkerOptions) {
    super(options);

    options = { ...DEFAULT_OPTIONS, ...options };

    this.type = options.type;
    this.style = options.style;
    this.badge = options.badge;
    this.draggable = options.draggable;
    this.feature = options.feature;
    this.iconLabel = options.iconLabel;

    this.label = typeof options.label === 'string' ? { text: options.label } : options.label;

    this._iconAnchor = new SnPoint(0, 0);
    this.icon =
      typeof options.icon === 'string' ? { name: options.icon, color: '#000' } : options.icon;

    this._iconMarker = this._createIconMarker();
    this._googleMapMarker = this._createGoogleMapMarker();
    this._iconMarker.setIconLabel(options.iconLabel);
    this._destination = this.getLonLat()?.toArray();
  }

  public getMarker(): google.maps.Marker {
    return this._googleMapMarker;
  }

  public distanceInMiles(lat: number, lng: number): number {
    const meters2Miles = 0.000621371192;
    const distance = google.maps.geometry.spherical.computeDistanceBetween(
      new google.maps.LatLng(this.lonLat.lat, this.lonLat.lon),
      new google.maps.LatLng(lat, lng),
    );
    return distance * meters2Miles;
  }

  public setFollowMode(followMode: boolean): void {
    this._followMode = followMode;
  }

  public getFollowMode(): boolean {
    return this._followMode;
  }

  public setDestination(dest: SnLonLatLike): void {
    this.setLonLat(this._destination);
    this._destination = dest;
  }

  public setDeltas(seconds: number): void {
    const destination = SnLonLat.convert(this._destination);
    const current = this.getLonLat();
    this._expectedMoves = 10 * seconds;
    this._actualMoves = 0;
    this._deltaLat = (destination.lat - current.lat) / this._expectedMoves;
    this._deltaLng = (destination.lon - current.lon) / this._expectedMoves;
  }

  public move(): void {
    if (this._actualMoves >= this._expectedMoves) {
      this.setLonLat(this._destination);
      return;
    }
    this._actualMoves++;
    const current = this.getLonLat();
    const destination = SnLonLat.convert(this._destination);
    if (!this.map.contains(current)) {
      this._actualMoves = this._expectedMoves;
      this.setLonLat(destination);
    }
    let newLat = current.lat + this._deltaLat;
    if (current.lat === destination.lat) {
      newLat = destination.lat;
    }
    let newLng = current.lon + this._deltaLng;
    if (current.lon === destination.lon) {
      newLng = destination.lon;
    }
    this.setLonLat([newLng, newLat]);
    if (this.getFollowMode()) {
      this.map.panTo(this.getLonLat());
    }
  }

  public setPopup(popup: GoogleMapPopup): void {
    if (this._popup) {
      this._popup.close();
    }
    this._popup = popup;
  }

  public getType(): GoogleMapMarkerType {
    return this.type;
  }

  public setType(type: GoogleMapMarkerType): this {
    if (this.type !== type) {
      this.type = type;
      this._updateMarkerStyles();

      this.fire('typeChange', type);
    }

    return this;
  }

  public getStyle(): IGoogleMapMarkerStyleOptions {
    return this.style;
  }

  public setStyle(style: IGoogleMapMarkerStyleOptions): this {
    this.style = {
      ...this.style,
      ...style,
    };
    this._updateMarkerStyles();
    this.fire('styleChange');
    return this;
  }

  public getFeatures(): any {
    return this.feature;
  }

  public getIcon(): GoogleMapMarkerIcon | any {
    return this.icon;
  }

  public setIcon(icon: GoogleMapMarkerIcon): this {
    this.icon = typeof icon === 'string' ? { name: icon, color: '#000' } : icon;

    this._updateIcon();

    return this;
  }

  public getBadge(): SnBadgeConfig {
    return this.badge;
  }

  public setBadge(badge: SnBadgeConfig): this {
    this.badge = badge;
    this._updateBadge();

    return this;
  }

  public getLabel(): IGoogleMapMarkerLabel {
    return this.label;
  }

  public getIconLabel(): any {
    return this.iconLabel;
  }

  public setLabel(label: string | IGoogleMapMarkerLabel): this {
    this.label = typeof label === 'string' ? { text: label } : label;

    this._updateLabel();

    return this;
  }

  public getDraggable(): boolean {
    return this.draggable;
  }

  public setDraggable(draggable: boolean): this {
    if (this.draggable !== draggable) {
      this.draggable = draggable;
      this._googleMapMarker.setDraggable(draggable);

      this.fire('draggableChange', draggable);
    }

    return this;
  }

  public setRotation(deg: number): void {
    this._iconMarker.setRotation(deg);
  }

  public setPulse(colors: string[], delay: number, fontSet?: string, fontIcon?: string): void {
    if (this.pulseInterval) {
      clearInterval(this.pulseInterval);
    }
    let i = 0;
    const change = () => {
      const color = colors;
      this.setIcon({
        fontSet,
        fontIcon,
        color: '#000',
      });
      this.setStyle({
        fillColor: color[i],
      });
      i = (i + 1) % color.length;
    };
    this.pulseInterval = setInterval(change, delay);
  }

  public animateSetLonLat(seconds: number, moveto: SnLonLatLike): void {
    const destination = SnLonLat.convert(moveto);
    if (!this.map.contains(destination)) {
      clearInterval(this.interval);
      this.setLonLat(destination);
      return;
    }
    const current = this.getLonLat();
    let lat = current.lat;
    let lng = current.lon;
    clearInterval(this.interval);
    const deltalat = (destination.lat - lat) / 100;
    const deltalng = (destination.lon - lng) / 100;
    if (destination.lat === lat && destination.lon === lng) {
      return;
    }
    const delay = 10 * seconds;
    let count = 0;
    this.interval = setInterval(() => {
      if (deltalat === 0 && deltalng === 0) {
        clearInterval(this.interval);
      }
      lat += deltalat;
      lng += deltalng;
      this.setLonLat([lng, lat]);
      if (this.getFollowMode()) {
        this.map.panTo(this.getLonLat());
      }
      count++;
      if (count === 100) {
        clearInterval(this.interval);
      }
    }, delay);
  }

  protected draw(): void {}

  protected onAdd(): void {
    this._updateIcon();
    this._updateBadge();
    this._updateTitle();
    this._updateLabel();
    this._updateZIndex();
    this._updateOpacity();
    this._updatePosition();
    this._updateMarkerStyles();

    this._iconMarker.addTo(this.map);
    this._googleMapMarker.setMap(this.map.mapInstance);

    this._initEventListenners();
  }

  protected onRemove(): void {
    clearInterval(this.interval);
    clearInterval(this.pulseInterval);
    this.timeouts.forEach((timeout) => clearTimeout(timeout));
    this.timeouts = [];

    this._iconMarker.remove();
    this._googleMapMarker.setMap(null);
    if (this._popup) {
      this._popup.close();
    }
    this._destroyEventListenners();
  }

  public remove(): this {
    // Always call onRemove() to ensure proper cleanup even if the marker was never added to the map
    this.onRemove();

    return super.remove();
  }

  private _updatePosition(): void {
    if (!this.lonLat) {
      return;
    }

    const lonLat = this.getLonLat();
    const latLon = toGoogleMapLatLng(lonLat);
    this._iconMarker.setLonLat(lonLat);
    this._googleMapMarker.setPosition(latLon);
    if (this._popup) {
      this._popup.setLonLat(lonLat);
    }
  }

  private _updateZIndex(): void {
    const zIndex = this.getZIndex();

    this._iconMarker.setZIndex(zIndex + 1);
    this._googleMapMarker.setZIndex(zIndex);
  }

  private _updateIcon(): void {
    if (!this.icon) {
      this._iconMarker.setIcon(null);
    } else if (typeof this.icon === 'string') {
      this._iconMarker.setIcon(this.icon);
      this._iconMarker.setColor('#000');
    } else {
      if ('name' in this.icon) {
        this._iconMarker.setIcon(this.icon.name);
      } else if ('fontIcon' in this.icon) {
        this._iconMarker.setIcon({
          fontIcon: this.icon.fontIcon,
          fontSet: this.icon.fontSet,
          size: this.icon.size,
        });
      } else if ('fontIconUnicode' in this.icon) {
        this._iconMarker.setIcon({
          fontIconUnicode: this.icon.fontIconUnicode,
          fontSet: this.icon.fontSet,
        });
      }

      this._iconMarker.setColor(this.icon.color);
    }

    this._iconMarker.setAnchor(this._iconAnchor);
  }

  private _updateBadge(): void {
    this._iconMarker.setBadge(this.badge);
  }

  private _updateTitle(): void {
    const title = this.getTitle();
    this._googleMapMarker.setTitle(title);
  }

  private _updateLabel(): void {
    if (this.label) {
      this._googleMapMarker.setLabel({
        fontSize: '12px',
        ...this.label,
      });
    } else {
      this._googleMapMarker.setLabel(null);
    }
  }

  private _updateOpacity(): void {
    const opacity = this.getOpacity();

    this._iconMarker.setOpacity(opacity);
    this._googleMapMarker.setOpacity(opacity);
  }

  private _updateMarkerStyles(): void {
    let path: string;
    let url: string;
    let labelOrigin: google.maps.Point;
    let size: google.maps.Size;
    let scaledSize: google.maps.Size;

    switch (this.type) {
      case GoogleMapMarkerType.PIN:
        this._iconAnchor = new SnPoint(0, -29);

        labelOrigin = new google.maps.Point(0, 6);
        path =
          'M0,-34 c-6.634,0,-12,5.321,-12,11.9 c0,8.925,12,22.1,12,22.1 s12,-13.175,12,-22.1 c0,-6.579,-5.366,-11.9,-12,-11.9z';
        url = null;
        size = null;
        scaledSize = null;
        break;
      case GoogleMapMarkerType.SQUARE:
        this._iconAnchor = new SnPoint(0, 0);

        labelOrigin = new google.maps.Point(0, 20);
        path =
          'M-8,-12 a4,4,0,0,0,-4,4 v16 a4,4,0,0,0,4,4 h16 a4,4,0,0,0,4,-4 v-16 a4,4,0,0,0,-4,-4, h-16z';
        url = null;
        size = null;
        scaledSize = null;
        break;
      case GoogleMapMarkerType.CIRCLE:
        this._iconAnchor = new SnPoint(0, 0);

        labelOrigin = new google.maps.Point(0, 20);
        path = 'M0,0 m-12,0 a12,12,0,1,0,24,0 a12,12,0,1,0,-24,0';
        url = null;
        size = null;
        scaledSize = null;
        break;
      case GoogleMapMarkerType.IMAGE:
        this._iconAnchor = new SnPoint(0, 0);

        labelOrigin = new google.maps.Point(0, 20);
        path = null;
        url = this.style.url;
        size = this.style.size;
        scaledSize = this.style.scaledSize;
        break;
      case GoogleMapMarkerType.CIRCLE_PIN:
        this._iconAnchor = new SnPoint(0, -21);

        labelOrigin = new google.maps.Point(0, 8);
        path =
          'M0,-27 c-6.627,0,-12,5.44,-12,12.15 c0,5.63,3.787,10.354,8.92,11.731 l3.08,3.119 l3.08,-3.119 c5.133,-1.376,8.92,-6.101,8.92,-11.731 c0,-6.71,-5.373,-12.15,-12,-12.15z';
        url = null;
        size = null;
        scaledSize = null;
        break;
    }

    const scale = (this.style && this.style.scale) || 1;
    const rotate = (this.style && this.style.rotation) || 0;

    this._iconMarker.setRotation(rotate);

    this._iconMarker.setScale(scale * 0.7);

    this._iconMarker.setAnchor(this._iconAnchor);
    if (this.style.fillOpacity === 0 && this.style.strokeOpacity === 0) {
      this._iconMarker.setScale(scale);
    }

    this._googleMapMarker.setIcon({
      path,
      url,
      size,
      scaledSize,
      labelOrigin,
      fillOpacity: 1,
      fillColor: COLORS['grey-0'],
      strokeColor: COLORS['grey-0'],
      strokeWeight: 1.5,
      ...this.style,
    });
  }

  private _createGoogleMapMarker(): google.maps.Marker {
    return new google.maps.Marker({
      title: this.getTitle(),
      zIndex: this.getZIndex(),
      opacity: this.getOpacity(),
      clickable: this.getClickable(),
      draggable: this.getDraggable(),
      crossOnDrag: false,
    });
  }

  private _createIconMarker(): GoogleMapIconMarker {
    return new GoogleMapIconMarker({
      name: '',
      clickable: false,
    });
  }

  private _initEventListenners(): void {
    this._googleEventListenners.push(
      this._googleMapMarker.addListener('click', this._onClick.bind(this)),
      this._googleMapMarker.addListener('dblclick', this._onDblclick.bind(this)),
      this._googleMapMarker.addListener('rightclick', this._onRightclick.bind(this)),
      this._googleMapMarker.addListener('dragstart', this._onDragstart.bind(this)),
      this._googleMapMarker.addListener('drag', this._onDrag.bind(this)),
      this._googleMapMarker.addListener('dragend', this._onDragend.bind(this)),
      this._googleMapMarker.addListener('mouseover', this._onMouseOver.bind(this)),
      this._googleMapMarker.addListener('mouseout', this._onMouseOut.bind(this)),
    );

    this.on('titleChange')
      .pipe(takeUntil(this.on('remove')))
      .subscribe(this._updateTitle.bind(this));

    this.on('zIndexChange')
      .pipe(takeUntil(this.on('remove')))
      .subscribe(this._updateZIndex.bind(this));

    this.on('lonLatChange')
      .pipe(takeUntil(this.on('remove')))
      .subscribe(this._updatePosition.bind(this));

    this.on('opacityChange')
      .pipe(takeUntil(this.on('remove')))
      .subscribe(this._updateOpacity.bind(this));
  }

  private _destroyEventListenners(): void {
    clearInterval(this.interval);
    this._googleEventListenners.forEach((event) => event.remove());
    this._googleEventListenners.length = 0;
  }

  private _onClick(event: MouseEvent): void {
    this.fire('click', event, true);
  }

  private _onMouseOver(event: MouseEvent): void {
    this.fire('mouseover', event, true);
  }

  private _onMouseOut(event: MouseEvent): void {
    this.fire('mouseout', event, true);
  }

  private _onDblclick(event: MouseEvent): void {
    this.fire('dblclick', event, true);
  }

  private _onRightclick(event: MouseEvent): void {
    this.fire('rightclick', event, true);
  }

  private _onDragstart(): void {
    this._iconMarker.setZIndex(1000001);
    this.fire('dragstart');
  }

  private _onDrag(): void {
    this.lonLat = fromGoogleMapLatLng(this._googleMapMarker.getPosition());
    this._iconMarker.setLonLat(this.lonLat);

    this.fire('drag', this.lonLat);
  }

  private _onDragend(): void {
    this._updateZIndex();

    this.fire('dragend', this.lonLat);
  }
}
